import React, { useState, useEffect } from 'react';
import {
  FileText,
  DollarSign,
  TrendingUp,
  Users,
  RefreshCw,
  Calendar,
  ArrowUpRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';

interface DashboardStats {
  blotters: {
    total: number;
    this_month: number;
    by_status: Record<string, number>;
  };
  transactions: {
    total: number;
    this_month: number;
    completed: number;
    pending: number;
  };
  commissions: {
    total_commission: number;
    total_payout: number;
    net_commission: number;
    this_month: {
      total_commission: number;
      total_payout: number;
    };
  };
  users: {
    total: number;
    active: number;
  };
  recent_activity: {
    latest_blotter: {
      id: number;
      date: string;
      status: string;
      total_commission: number;
    } | null;
  };
}

interface TopPerformer {
  user_id: number;
  user_name: string;
  total_earned: number;
}

interface TopClient {
  client_id: number;
  client_name: string;
  total_commission: number;
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([]);
  const [topClients, setTopClients] = useState<TopClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadDashboardData = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);

    try {
      const [overviewRes, performersRes, clientsRes] = await Promise.all([
        apiService.getDashboardOverview(),
        apiService.getTopPerformers(),
        apiService.getTopClients()
      ]);

      if (overviewRes?.data) {
        setStats(overviewRes.data);
      }
      if (performersRes?.data) {
        setTopPerformers(performersRes.data || []);
      }
      if (clientsRes?.data) {
        setTopClients(clientsRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
      if (showRefreshIndicator) setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleRefresh = () => {
    loadDashboardData(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Commission Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Overview of trading commissions and payouts
            </p>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 bg-white"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Commission */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Commission</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats?.commissions.total_commission || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats?.transactions.total || 0} trades
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Total Payout */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Payout</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats?.commissions.total_payout || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Employee commissions
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <ArrowUpRight className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Net Commission */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Net Commission</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats?.commissions.net_commission || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  After payouts
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Total Blotters */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Blotters</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats?.blotters.total || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats?.blotters.this_month || 0} this month
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* This Month Stats */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            This Month
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats?.commissions.this_month.total_commission || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Commission Earned</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(stats?.commissions.this_month.total_payout || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Employee Payout</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">
                {stats?.transactions.this_month || 0}
              </p>
              <p className="text-sm text-gray-600 mt-1">Transactions</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">
                {stats?.blotters.this_month || 0}
              </p>
              <p className="text-sm text-gray-600 mt-1">Blotters Processed</p>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Employees */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-500" />
                Top Employees
              </h2>
              <button
                onClick={() => navigate('/reports/user')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View All
              </button>
            </div>

            {topPerformers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No commission data yet</p>
            ) : (
              <div className="space-y-3">
                {topPerformers.slice(0, 5).map((performer, index) => (
                  <div key={performer.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-900">{performer.user_name}</span>
                    </div>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(performer.total_earned)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Clients */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gray-500" />
                Top Clients
              </h2>
              <button
                onClick={() => navigate('/clients')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View All
              </button>
            </div>

            {topClients.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No transaction data yet</p>
            ) : (
              <div className="space-y-3">
                {topClients.slice(0, 5).map((client, index) => (
                  <div key={client.client_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <p className="font-medium text-gray-900">{client.client_name}</p>
                    </div>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(client.total_commission)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Latest Blotter Info */}
        {stats?.recent_activity.latest_blotter && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Latest Blotter</h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <FileText className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {formatDate(stats.recent_activity.latest_blotter.date)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Status: <span className={`font-medium ${
                      stats.recent_activity.latest_blotter.status === 'completed' ? 'text-green-600' :
                      stats.recent_activity.latest_blotter.status === 'error' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {stats.recent_activity.latest_blotter.status.charAt(0).toUpperCase() +
                       stats.recent_activity.latest_blotter.status.slice(1)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(stats.recent_activity.latest_blotter.total_commission)}
                </p>
                <button
                  onClick={() => navigate(`/reports/blotter?date=${stats.recent_activity.latest_blotter?.date.split('T')[0]}`)}
                  className="text-sm text-blue-600 hover:text-blue-800 mt-1"
                >
                  View Report
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
